/**
 * Sound Map Store
 *
 * Zustand store for the learner's per-phoneme mastery map
 * (GET /api/speech/sound-map). States are computed server-side —
 * this store never re-derives thresholds; it only caches the response.
 *
 * Consumed by SoundMapScreen (full map) and the Practice tab's
 * SoundMapSummaryCard (summary counts). Both load on focus; the card
 * silently keeps stale data / falls back to a neutral CTA on failure.
 *
 * @see docs/specs/mobile/sound-map.mobile.md
 * @see docs/specs/api/sound-map.api.md
 */

import { create } from 'zustand';
import { api } from '../services/api';
import { createLogger } from '../utils/logger';
import type { StoreErrorInfo } from '../generated/api/endpoint-errors';
import type { SoundMapResponse } from '../generated/api/http-contracts';

const log = createLogger('SoundMapStore');

interface SoundMapState {
  /** Last successfully fetched map — kept on refresh failure. */
  data: SoundMapResponse | null;
  loading: boolean;
  errorInfo: StoreErrorInfo | null;

  /** Fetch the sound map. No-ops while a fetch is already in flight. */
  loadSoundMap: () => Promise<void>;
  clearError: () => void;
}

export const useSoundMapStore = create<SoundMapState>((set, get) => ({
  data: null,
  loading: false,
  errorInfo: null,

  loadSoundMap: async () => {
    if (get().loading) return;
    set({ loading: true, errorInfo: null });
    const result = await api.getSoundMap();
    if (result.ok) {
      set({ data: result.data, loading: false });
    } else {
      // Keep any previously loaded data — a failed refresh is non-fatal
      // for consumers that already have a map to show.
      log.warn('Failed to load sound map', { kind: result.kind });
      set({ loading: false, errorInfo: result });
    }
  },

  clearError: () => set({ errorInfo: null }),
}));

// ─── Selectors (individual — CHAT_PERFORMANCE.md rule) ──────────────

export const selectSoundMapData = (s: SoundMapState) => s.data;
export const selectSoundMapLoading = (s: SoundMapState) => s.loading;
export const selectSoundMapErrorInfo = (s: SoundMapState) => s.errorInfo;
export const selectLoadSoundMap = (s: SoundMapState) => s.loadSoundMap;
export const selectClearSoundMapError = (s: SoundMapState) => s.clearError;
